import { SidebarTab } from "./SidebarTab";
import {
  Accordion,
  CloseButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { Stack } from "@chakra-ui/react";
import { useActive } from "../hooks/useActive.hook";
import { useFilteredMenu } from "../hooks/useFilteredMenu";
import { SearchIcon } from "@chakra-ui/icons";
import { useMemo, useState } from "react";

const normalizeSearchValue = (value = "") => value.toLowerCase().trim();

const Sidebar = () => {
  const { isActive } = useActive();
  const { colorMode } = useColorMode();
  const filteredMenu = useFilteredMenu();
  const [searchTerm, setSearchTerm] = useState("");
  const normalizedSearchTerm = normalizeSearchValue(searchTerm);

  const searchedMenu = useMemo(() => {
    if (!normalizedSearchTerm) {
      return filteredMenu;
    }

    return filteredMenu.reduce((menuItems, item) => {
      const headingMatches = normalizeSearchValue(item.heading).includes(
        normalizedSearchTerm,
      );

      if (headingMatches) {
        menuItems.push(item);
        return menuItems;
      }

      const matchingSubmenu = (item.submenu || []).filter((subItem) =>
        normalizeSearchValue(subItem.label).includes(normalizedSearchTerm),
      );

      if (matchingSubmenu.length > 0) {
        menuItems.push({ ...item, submenu: matchingSubmenu });
      }

      return menuItems;
    }, []);
  }, [filteredMenu, normalizedSearchTerm]);

  const searchAccordionIndexes = useMemo(() => {
    if (!normalizedSearchTerm) {
      return undefined;
    }

    let accordionIndex = -1;

    return searchedMenu.reduce((openIndexes, item) => {
      if (!item.submenu?.length) {
        return openIndexes;
      }

      accordionIndex += 1;
      openIndexes.push(accordionIndex);
      return openIndexes;
    }, []);
  }, [searchedMenu, normalizedSearchTerm]);

  const accordionProps = normalizedSearchTerm
    ? { allowMultiple: true, defaultIndex: searchAccordionIndexes }
    : { allowToggle: true };

  return (
    <Stack
      display={["none", "none", "none", "flex"]}
      bg={useColorModeValue("brand.accent", "black")}
      color={useColorModeValue("black", "white")}
      userSelect={"none"}
      w={270}
      minW={270}
      overflow={"auto"}
      pt={6}
      gap={2}
      zIndex={999}
    >
      <Text
        fontSize={"1.5rem"}
        textAlign={"center"}
        fontWeight={800}
        color={"white"}
        mb={4}
      >
        Prima Travels
      </Text>
      <InputGroup px={3} mb={2}>
        <InputLeftElement pointerEvents="none" left={3}>
          <SearchIcon color="whiteAlpha.900" />
        </InputLeftElement>
        <Input
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search menu"
          borderColor="whiteAlpha.700"
          color="white"
          _placeholder={{ color: "whiteAlpha.800" }}
          _hover={{ borderColor: "white" }}
          _focus={{
            borderColor: "white",
            boxShadow: "0 0 0 1px white",
          }}
          bg="whiteAlpha.100"
          borderRadius={4}
        />
        {searchTerm && (
          <InputRightElement right={3}>
            <CloseButton
              size="sm"
              color="white"
              onClick={() => setSearchTerm("")}
            />
          </InputRightElement>
        )}
      </InputGroup>
      <Accordion key={normalizedSearchTerm || "sidebar-menu"} {...accordionProps}>
        {searchedMenu.map((item, index) => (
          <SidebarTab
            data={item}
            key={index}
            color={isActive("/") || colorMode === "dark" ? "#000" : "#ffffff"}
          />
        ))}
      </Accordion>
      {normalizedSearchTerm && searchedMenu.length === 0 && (
        <Text px={4} py={2} color="whiteAlpha.900">
          No menu found
        </Text>
      )}
    </Stack>
  );
};

export { Sidebar };
